import React from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, X } from "lucide-react";

interface MultiEntryFieldProps<T> {
  entries: T[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (index: number, field: string, value: string) => void;
  labelOptions: readonly string[];
  labelField: string;
  renderInput: (entry: T, index: number) => React.ReactNode;
  addLabel: string;
  sectionLabel: string;
}

export function MultiEntryField<T extends Record<string, unknown>>({
  entries,
  onAdd,
  onRemove,
  onChange,
  labelOptions,
  labelField,
  renderInput,
  addLabel,
  sectionLabel,
}: MultiEntryFieldProps<T>) {
  return (
    <div className="space-y-2">
      <span className="text-xs text-muted-foreground font-medium">
        {sectionLabel}
      </span>
      <div className="flex flex-col gap-2">
        {entries.map((entry, index) => (
          <div key={index} className="flex items-center gap-2">
            <Select
              value={entry[labelField] as string}
              onValueChange={(value) => onChange(index, labelField, value)}
            >
              <SelectTrigger className="w-20 h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {labelOptions.map((option) => (
                  <SelectItem key={option} value={option} className="text-xs">
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex-1">{renderInput(entry, index)}</div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={() => onRemove(index)}
              disabled={entries.length <= 1}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 text-xs text-muted-foreground px-1"
        onClick={onAdd}
      >
        <Plus className="h-3 w-3 mr-1" />
        {addLabel}
      </Button>
    </div>
  );
}
